<template>
  <div
    class="input"
    :class="allClasses"
  >
    <input
      v-model="inputValue"
      :readonly="asButton"
      class="input__element"
      data-test-id="input-input"
      v-bind="attrs"
      v-on="listeners"
    >
    <div class="input__addon">
      <EIcon v-if="icon" class="input__icon" :icon="icon" :size="iconSize" />
    </div>
  </div>
</template>

<script>
import classes from '~~/common/vue/mixins/classes';

import EIcon from '~~/common/vue/components/icon';

export default {
  name: 'EInput',
  components: {
    EIcon,
  },
  mixins: [classes],
  props: {
    asButton: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    model: {
      type: String,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    icon: {
      type: String,
      default: null,
    },
    iconSize: {
      type: String,
      default: 'm',
    },
    placeholder: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    invalid: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
  },
  data() {
    return {
      inputValue: this.value,
      dirty: false,
    };
  },
  computed: {
    attrs() {
      const { name, type, placeholder, required, disabled, $attrs } = this;
      return { ...$attrs, name, type, placeholder, required, disabled };
    },
    listeners() {
      const { input, ...rest } = this.$listeners;
      return rest;
    },
    allClasses() {
      const allClasses = this.classes;
      if (this.dirty) {
        allClasses.push('input--dirty');
      }
      if (this.icon) {
        allClasses.push('input--has-icon');
      }
      if (this.type === 'hidden') {
        allClasses.push('input--hidden');
      }
      return allClasses;
    },
  },
  watch: {
    inputValue(val) {
      this.dirty = true;
      this.$emit('input', val);
    },
    value(val) {
      this.inputValue = val;
    },
  },
};
</script>

<style lang="scss" scoped>
$icon-padding-right: $space-s;

.input {
  appearance: none;
  position: relative;

  &__element {
    $padding-unit: $space-s + $space-2s;

    display: block;
    width: 100%;
    height: $space-3l;
    padding: 0 $space-l 0 $padding-unit;
    border: solid $border-width var(--color-form-border-default);
    border-radius: $border-radius-1l;
    background: var(--color-button-background-secondary);
    color: var(--color-button-content-secondary);
    font-family: $family;
    font-size: $font-m;
    line-height: $line-height-s;
    box-sizing: border-box;
    transition: border $timing-2s $easing;

    &::placeholder {
      color: var(--color-button-content-tertiary);
      opacity: 1;
    }

    &:hover {
      border-color: var(--color-form-border-hover);
    }

    &:focus {
      @extend %keyboard-nav-focus;

      border-color: var(--color-form-border-primary-active);
    }

    &:disabled {
      background: var(--color-background-primary);
      border-color: var(--color-disabled);
      color: var(--color-disabled);
      cursor: not-allowed;
    }
  }

  &__addon {
    position: absolute;
    top: 50%;
    right: $icon-padding-right;
    transform: translateY(-50%);
  }

  &__icon {
    pointer-events: none;
  }

  &--has-icon {
    .input__element {
      padding-right: 41px;
    }
  }

  &--as-button {
    .input__element {
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
      }
    }
  }

  &--hidden {
    display: none;
  }

  &--disabled {
    .input__icon {
      color: var(--color-disabled);
    }
  }

  // to make stylelint happy we'd have to break up the following block :(
  // stylelint-disable-next-line no-descending-specificity
  &--invalid .input__element,
  &--dirty .input__element:invalid {
    border-color: var(--color-error);
  }
}
</style>

<docs>
The Input Component

### States

#### Default

```js
let m;

<div>
  <EInput name="test" v-model="m" placeholder="Type some text" minlength="10" />
  <br/>
  <br/>
  The value is {{m}}
</div>
```

#### Disabled

```js
<EInput name="test" disabled placeholder="Type some text" />
```

#### Invalid

```js
<EInput name="test" invalid placeholder="Type some text" />
```

#### Icon

```js
<div>
<EInput name="test" icon="close" value="Standard" />
<br/>
<EInput name="test" icon="close" value="Disabled" disabled/>
</div>
```
</docs>
